import { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { OnChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import { LetDirective } from '@ngrx/component';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import type {
  EmailTemplate,
  EmailTemplatesEventInvitation,
} from '@recruitee/email-templates-types';
import { GdprAnalyticsService, GdprService } from '@recruitee/gdpr-data';
import { TranslatePipe } from '@recruitee/i18n';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  IconComponent,
  ButtonComponent,
} from '@recruitee/user-interface';

import type { RedactorComponent } from '../../redactor/redactor.component';
import type { RedactorInsertsStep } from '../../redactor/types';
import type { WysiwygLink } from '../../wysiwyg-extensions/link/wysiwyg-link.types';
import { WysiwygLinkType } from '../../wysiwyg-extensions/link/wysiwyg-link.types';
import { RedactorInsertsDropdownComponent } from './redactor-inserts-dropdown/redactor-inserts-dropdown.component';

@Component({
  selector: 'rt-redactor-inserts-button',
  templateUrl: './redactor-inserts-button.component.html',
  styleUrls: ['./redactor-inserts-button.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GdprAnalyticsService],
  imports: [
    ButtonComponent,
    OverlayOriginDirective,
    IconComponent,
    ConnectedOverlayDirective,
    LetDirective,
    RedactorInsertsDropdownComponent,
    TranslatePipe,
  ],
})
export class RedactorInsertsButtonComponent implements OnChanges {
  public readonly dropdownPositions = [
    new ConnectionPositionPair(
      { originX: 'end', originY: 'bottom' },
      { overlayX: 'end', overlayY: 'top' },
    ),
  ];

  @Input() public inserts: RedactorInsertsStep[];
  @Input() public input?: RedactorComponent;
  @Input() public emailTemplateInsideTemplate?: TemplateRef<any>;

  @Output() public saveAsTemplate = new EventEmitter<void>();
  @Output() public emailTemplateChoose: EventEmitter<
    EmailTemplate | EmailTemplatesEventInvitation
  > = new EventEmitter();

  public canManageCandidates: boolean = false;
  public filteredInserts$: BehaviorSubject<RedactorInsertsStep[]> = new BehaviorSubject<
    RedactorInsertsStep[]
  >([]);

  private canScheduleEvents = false;
  private ngOnDestroy$ = new Subject<void>();
  private REDACTOR_REQUIRED_INSERTS: RedactorInsertsStep[] = [
    'questionnaires-templates',
    'schedules',
    'gdpr-links',
  ];

  constructor(
    protected cdRef: ChangeDetectorRef,
    private gdprAnalyticsService: GdprAnalyticsService,
    private gdprSvc: GdprService,
    private store: Store,
  ) {}

  public ngOnInit(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$))
      .subscribe(abilityMap => {
        this.canScheduleEvents = abilityMap[Ability.ScheduleEvents];
        this.canManageCandidates = abilityMap[Ability.ManageCandidates];
        this.setFilteredInserts();
      });
  }

  public ngOnChanges(): void {
    this.setFilteredInserts();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
  public openDropdown(event: MouseEvent, dropdownRef: ConnectedOverlayDirective): void {
    event.preventDefault();
    event.stopPropagation();
    dropdownRef.open = true;
    dropdownRef.updatePosition();
    this.cdRef.detectChanges();
  }

  public insertLink(link: WysiwygLink): void {
    if (!this.input) return;

    const linkType = link.type!;
    const isGdprLink = [
      WysiwygLinkType.GdprChange,
      WysiwygLinkType.GdprRemove,
      WysiwygLinkType.GdprConsentRequest,
    ].includes(linkType);
    if (isGdprLink) {
      const eventKind = <'correctionRequest' | 'removalRequest' | 'consentRequestLink'>{
        [WysiwygLinkType.GdprChange]: 'correctionRequest',
        [WysiwygLinkType.GdprRemove]: 'removalRequest',
        [WysiwygLinkType.GdprConsentRequest]: 'consentRequestLink',
      }[<WysiwygLinkType.GdprChange | WysiwygLinkType.GdprRemove | WysiwygLinkType.GdprConsentRequest>linkType];

      this.gdprAnalyticsService.trackInsertGDPRLink(eventKind);
    }

    this.input.restoreSelection();
    this.input.insertAtCaret(
      {
        ...link,
      },
      'link',
    );
  }

  public saveSelection(e: MouseEvent) {
    e.preventDefault();
    if (this.input) {
      this.input.saveSelection();
    }
  }

  private setFilteredInserts(): void {
    let filteredInserts = this.canScheduleEvents
      ? this.inserts
      : this.inserts.filter(i => i !== 'schedules');

    filteredInserts = this.input
      ? filteredInserts
      : filteredInserts.filter(insert => !this.REDACTOR_REQUIRED_INSERTS.includes(insert));

    if (!this.gdprSvc.gdprSettingEnabled || !this.canManageCandidates) {
      filteredInserts = filteredInserts.filter((i: RedactorInsertsStep) => i !== 'gdpr-links');
    }
    this.filteredInserts$.next(filteredInserts);
  }
}
